import { bayer } from "../../intro/pixel_raster"
import { pack, RGB, unpack } from "./night_view"

/**
 * The chapter's title in pixel letters, the way the "samsara" wordmark is made (pixel_wordmark.ts,
 * packages/tools/src/wordmark/bake.mjs): Jost rasterized 8x over by the browser itself, each art
 * pixel keeping how much of it the letters cover, in tenths. The wordmark bakes its four letters
 * offline; a title can be any words the server sends, so these are baked when the card opens, from
 * the same bundled font.
 *
 * Drawn soft, as the wordmark is: partial coverage becomes translucent steps, cream at the top of
 * the letters warming to gold at their feet, with a stepped glow behind.
 */

export interface TitleRaster {
    /** In art pixels. */
    w: number
    h: number
    /** Coverage per art pixel, 0..9 tenths, row by row. */
    cov: Uint8Array
    /** Rows from the top of the raster to the capitals' top and to the baseline. */
    capTop: number
    base: number
}

/** Rasterize a line at an x-height in art pixels, or undefined where there is no canvas to do it with. */
export type Rasterize = (text: string, xHeight: number) => TitleRaster | undefined

const SUPER = 8
const FONT = (size: number) => `400 ${size}px Jost, Verdana, sans-serif`
/** Jost's proportions, for a canvas whose TextMetrics lack the bounding-box fields. */
const JOST_X = 0.47
const JOST_CAP = 0.7

export const bakeInBrowser: Rasterize = (text, xHeight) => {
    if (typeof document === "undefined") return undefined
    const canvas = document.createElement("canvas")
    const g = canvas.getContext("2d")
    if (!g || typeof g.getImageData !== "function") return undefined
    g.font = FONT(200)
    const measured = (s: string, key: "actualBoundingBoxAscent") => {
        const v = g.measureText(s)[key]
        return typeof v === "number" && isFinite(v) && v > 0 ? v / 200 : undefined
    }
    const xRatio = measured("x", "actualBoundingBoxAscent") ?? JOST_X
    const capRatio = measured("H", "actualBoundingBoxAscent") ?? JOST_CAP
    const size = (xHeight * SUPER) / xRatio
    const track = Math.round(xHeight * 0.2) * SUPER
    g.font = FONT(size)
    // Letter by letter, kerned as the font kerns each pair, tracked on whole art pixels.
    const chars = [...text]
    const advance = chars.map(ch => g.measureText(ch).width)
    const at: number[] = []
    let x = 0
    chars.forEach((ch, i) => {
        at.push(x)
        const kern = i + 1 < chars.length ? g.measureText(ch + chars[i + 1]).width - advance[i] - advance[i + 1] : 0
        x += advance[i] + kern + (ch === " " ? 0 : track)
    })
    if (!(x > 0)) return undefined
    const asc = Math.ceil((capRatio * size) / SUPER) + 2
    const desc = Math.ceil((0.24 * size) / SUPER) + 1
    const wArt = Math.ceil(x / SUPER) + 4
    const hArt = asc + desc
    canvas.width = wArt * SUPER
    canvas.height = hArt * SUPER
    // Nudge the line by part of a pixel to the offset that leaves the fewest half-covered pixels.
    let best: { cov: Uint8Array; fuzzy: number } | undefined
    for (const ox of [0, 2, 4, 6]) {
        g.clearRect(0, 0, canvas.width, canvas.height)
        g.font = FONT(size)
        g.fillStyle = "#fff"
        g.textBaseline = "alphabetic"
        chars.forEach((ch, i) => g.fillText(ch, SUPER * 2 + ox + at[i], asc * SUPER))
        const data = g.getImageData(0, 0, canvas.width, canvas.height).data
        const cov = new Uint8Array(wArt * hArt)
        let fuzzy = 0
        for (let j = 0; j < hArt; j++) {
            for (let i = 0; i < wArt; i++) {
                let sum = 0
                for (let v = 0; v < SUPER; v++) for (let u = 0; u < SUPER; u++) sum += data[((j * SUPER + v) * canvas.width + i * SUPER + u) * 4 + 3]
                const t = Math.min(9, Math.floor((sum / (SUPER * SUPER * 255)) * 10))
                cov[j * wArt + i] = t
                if (t >= 3 && t <= 6) fuzzy++
            }
        }
        if (!best || fuzzy < best.fuzzy) best = { cov, fuzzy }
    }
    return cropped(best!.cov, wArt, hArt, asc - Math.round((capRatio * size) / SUPER), asc)
}

/** The raster trimmed to its ink, left and right. */
export const cropped = (cov: Uint8Array, w: number, h: number, capTop: number, base: number): TitleRaster | undefined => {
    let x0 = w, x1 = -1
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) if (cov[j * w + i] >= 2) { x0 = Math.min(x0, i); x1 = Math.max(x1, i) }
    if (x1 < 0) return undefined
    const cw = x1 - x0 + 1
    const out = new Uint8Array(cw * h)
    for (let j = 0; j < h; j++) for (let i = 0; i < cw; i++) out[j * cw + i] = cov[j * w + x0 + i]
    return { w: cw, h, cov: out, capTop, base }
}

/** The x-heights a title may take, largest first: a long title steps down rather than being scaled. */
export const TITLE_SIZES = [12, 11, 10, 9, 8]

/** The largest size of the title that fits `maxW` art pixels (the smallest if none does). */
export const fitTitle = (text: string, maxW: number, rasterize: Rasterize = bakeInBrowser): TitleRaster | undefined => {
    let last: TitleRaster | undefined
    for (const size of TITLE_SIZES) {
        last = rasterize(text, size)
        if (!last || last.w <= maxW) return last
    }
    return last
}

// ---- drawing ----------------------------------------------------------------------------------

const hex = (h: number): RGB => [(h >> 16) & 0xff, (h >> 8) & 0xff, h & 0xff]
const mixc = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const ease = (t: number) => 1 - Math.pow(1 - clamp01(t), 3)

/** Cream at the top of the letters warming toward gold at their feet: the wordmark's fill. */
const FILL = [0xfff4e2, 0xfcecd6, 0xf8dfbd, 0xf1cd9a].map(hex)
const EMBER = hex(0xe8501a)
const GOLD_HOT = hex(0xffc93c)
const GLOW = hex(0xc9b48f)
const GLOW_HOT = hex(0xffa64a)
const softAlpha = (t: number) => (t >= 7 ? 1 : t >= 4 ? 0.67 : t >= 2 ? 0.33 : 0)

/** How the letters catch: left to right over this long, each pixel dithered a little later. */
export const TITLE_SWEEP_MS = 420
const CATCH_MS = 140
const COOL_MS = 620

const GLOW_PAD = 3
/** The stepped glow behind the letters, by distance from their solid pixels: never a smooth falloff. */
export const titleGlow = (t: TitleRaster): Float32Array => {
    const gw = t.w + GLOW_PAD * 2
    const gh = t.h + GLOW_PAD * 2
    const glow = new Float32Array(gw * gh)
    const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < t.w && y < t.h && t.cov[y * t.w + x] >= 4
    for (let y = 0; y < gh; y++) {
        for (let x = 0; x < gw; x++) {
            let d = Infinity
            for (let j = -GLOW_PAD - 1; j <= GLOW_PAD + 1 && d > 0; j++) {
                for (let i = -GLOW_PAD - 1; i <= GLOW_PAD + 1; i++) if (solid(x - GLOW_PAD + i, y - GLOW_PAD + j)) d = Math.min(d, Math.hypot(i, j))
            }
            glow[y * gw + x] = d <= 1.5 ? 0.34 : d <= 2.5 ? 0.15 : d <= GLOW_PAD + 0.5 ? 0.06 : 0
        }
    }
    return glow
}

/** A buffer to draw into: art pixels, the rect's top-left at (x0, y0). */
export interface Canvas {
    out: Uint32Array
    x0: number
    y0: number
    w: number
    h: number
}
const blend = (c: Canvas, x: number, y: number, color: RGB, alpha: number) => {
    const i = x - c.x0, j = y - c.y0
    if (i < 0 || j < 0 || i >= c.w || j >= c.h || alpha <= 0) return
    const k = j * c.w + i
    c.out[k] = alpha >= 1 ? pack(color) : pack(mixc(unpack(c.out[k]), color, alpha))
}

/**
 * The title with its glow, its top-left at (x, y), `ms` after it starts to catch. `fade` (0..1)
 * dissolves it away on the way out.
 */
export const paintTitle = (c: Canvas, t: TitleRaster, glow: Float32Array, x: number, y: number, ms: number, fade: number) => {
    const gw = t.w + GLOW_PAD * 2
    for (let j = 0; j < t.h + GLOW_PAD * 2; j++) {
        for (let i = 0; i < gw; i++) {
            const a = glow[j * gw + i]
            if (!a) continue
            const px = x - GLOW_PAD + i, py = y - GLOW_PAD + j
            const lit = ms - (((i - GLOW_PAD) / t.w) * TITLE_SWEEP_MS + 120)
            if (lit < 0 || (fade > 0 && fade > bayer(px, py))) continue
            const heat = clamp01(1 - lit / 900)
            blend(c, px, py, mixc(GLOW, GLOW_HOT, heat), a * 0.5 * (1 + heat))
        }
    }
    for (let j = 0; j < t.h; j++) {
        for (let i = 0; i < t.w; i++) {
            const a = softAlpha(t.cov[j * t.w + i])
            if (!a) continue
            const px = x + i, py = y + j
            const age = ms - ((i / t.w) * TITLE_SWEEP_MS + bayer(px, py) * 120)
            if (age < 0 || (fade > 0 && fade > bayer(px, py))) continue
            const band = FILL[Math.max(0, Math.min(3, Math.floor(((j - t.capTop) / Math.max(1, t.base - t.capTop)) * 4)))]
            const color = age < CATCH_MS ? mixc(EMBER, GOLD_HOT, age / CATCH_MS) : age < CATCH_MS + COOL_MS ? mixc(GOLD_HOT, band, ease((age - CATCH_MS) / COOL_MS)) : band
            blend(c, px, py, color, a)
        }
    }
}

/** By this long after it starts to catch, every pixel of the title has cooled to its resting colour. */
export const TITLE_SETTLED_MS = TITLE_SWEEP_MS + 120 + CATCH_MS + COOL_MS

/**
 * The builder's lens rule under the title, drawn out from `cx` by `progress` (0..1), gold at the
 * middle stepping down to bronze at its ends, a nub above the middle, its tips bright while moving.
 */
export const paintRule = (c: Canvas, cx: number, y: number, half: number, progress: number, fade: number) => {
    if (progress <= 0) return
    const reach = Math.round(half * ease(progress))
    for (let dx = -reach; dx <= reach; dx++) {
        if (fade > 0 && fade > bayer(cx + dx, y)) continue
        const k = Math.abs(dx) / half + (bayer(cx + dx, y) - 0.5) * 0.12
        blend(c, cx + dx, y, hex(k < 0.16 ? 0xf2c46a : k < 0.42 ? 0xd9a85a : k < 0.7 ? 0x9f7845 : 0x5e4632), 1)
    }
    if (fade > 0) return
    for (const dx of [-1, 0, 1]) blend(c, cx + dx, y - 1, hex(0xf2c46a), 1)
    if (progress < 1) {
        for (const s of [-1, 1]) {
            blend(c, cx + s * reach, y, hex(0xfff1b0), 1)
            blend(c, cx + s * reach, y - 1, GOLD_HOT, 0.6)
            blend(c, cx + s * reach, y + 1, GOLD_HOT, 0.6)
        }
    }
}
